import React, { useMemo, useState } from "react";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import { useTranslation } from "next-i18next";
import { useSelector, useDispatch } from "react-redux";
import classnames from "classnames";
import { useRouter } from "next/router";

import Logotype from "@/components/Logotype/Logotype";
import Container from "@/components/Container/Container";
import LinkButton from "@/components/Button/LinkButton";
import ButtonShare from "../SharePopup/ButtonShare";

import { Button } from "@/components/index";
import IconView from "@/shared/components/IconView/IconView";
import EgwWebFont from "@/shared/assets/EgwWebFont";
import { LANDING_SECTION } from "@/views/BookLanding/BookLanding.constns";
import { NEXT_URLS } from "@/utils/routes/routes.constants";
import { usePagePropsContext } from "@/contexts/pageProps";
import PanelHeaderMenu from "@/components/Header/PanelHeaderMenu";
import { translateObjectsArray } from "@/shared/utils/multilang/i18n";
import { getMainAppHost } from "@/shared/utils/url/url";
import {
  EL_ID_PORTAL_HEADER_ACTIONS,
  EL_ID_PORTAL_HEADER_ACTIONS_LEFT,
} from "@/components/Header/HeaderLanding.constants";
import { MenuItems } from "@/utils/MenuItems.constants";
import { useAuthContext } from "@/contexts/auth";
import { systemActions } from "@/store/actions";
import { selectorShareData } from "@/shared/store/share/selectors";
import { BIG_LANDING_LINKS } from "@/views/BookLanding/BookLandingStatic.constans";
import { LANDING_SELECTORS } from "@/utils/landing/landing.constants";

import styles from "./HeaderLanding.module.scss";
import ButtonInstallPWA from "../Button/ButtonInstallPWA";
import LanguageUISelector from "../settingsInterface/LanguageUISelector";

const ButtonTheme = dynamic(() => import("@/components/DropDown/ButtonTheme"), {
  ssr: false,
});

const LINKS = [
  MenuItems.login,
  {
    href: "/#" + LANDING_SECTION.ABOUT,
    label: "aboutBook",
  },
  {
    href: "/#" + LANDING_SECTION.LANGUAGES,
    label: "languages",
  },
  {
    href: "/#" + LANDING_SECTION.AUTHOR,
    label: "author",
  },
  {
    href: getMainAppHost(),
    label: "moreBooks",
  },
  {
    isOnLineOnly: true,
    href: NEXT_URLS.contactUs,
    label: "contact",
    title: "contactUs",
  },
  MenuItems.logout,
];

const HeaderLanding = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch();
  const {
    landingBookViewConfig,
    isBigLanding,
    isSetRPRAC,
    isSetCONFLICT,
    isSetTESTIMONIES,
    landingBookId,
  } = usePagePropsContext();
  const { isLogin, login, logout } = useAuthContext();
  const [isOpen, setIsOpen] = useState(false);
  const shareData = useSelector(selectorShareData);
  const isReaderPage = router.asPath.includes(NEXT_URLS.read);

  const handleClickAuth = (e) => {
    e.preventDefault();

    if (isLogin) {
      logout();
    } else {
      login();
    }
  };

  const toggle = () => {
    setIsOpen((isOpen) => !isOpen);
  };

  const handleClickItem = (e, item) => {
    toggle();

    if (item && (item.id === MenuItems.login.id || item.id === MenuItems.logout.id)) {
      handleClickAuth(e);
    }
  };

  const btnInstallPWA = <ButtonInstallPWA className={styles["link"]} size={Button.size.default} />;

  const linksFinal = useMemo(() => {
    if (isBigLanding) {
      const links = [...BIG_LANDING_LINKS];
      if (isReaderPage) {
        links.shift();
        links.unshift({
          href: "/",
          label: "Home",
          id: "openHomeHeadMenuButton",
        });
      }
      return links;
    }

    const links = [...LINKS];
    const filtered = links.filter((l) => (!l.onlyAuth || isLogin) && (!l.onlyNoAuth || !isLogin));

    // TODO: Refactor it - move to landing.constants.js
    if (isSetRPRAC) {
      filtered.unshift({
        href: "https://adventistbookcenter.com/asi",
        label: "buy",
      });
    }
    
    if (isSetRPRAC || isSetCONFLICT || (landingBookId === "b2017")) {
      filtered.splice(
        filtered.findIndex((link) => link.label === "languages"),
        1,
      );
    }

    if (isSetRPRAC || isSetCONFLICT || isSetTESTIMONIES) {
      filtered.splice(
        filtered.findIndex((link) => link.label === MenuItems.login.label),
        1,
      );
    }

    return translateObjectsArray(filtered, t, {
      keyOrKeys: ["label", "title"],
    });
  }, [t, isLogin, isBigLanding, isSetRPRAC, isSetCONFLICT, isSetTESTIMONIES, isReaderPage, LINKS]);

  const jsxLinks = linksFinal.map(({ id, href, isOnLineOnly, label, title, isExternal }) => {
    if (id === MenuItems.login.id || id === MenuItems.logout.id) {
      return null;
    }

    return (
      <LinkButton
        key={id ?? href}
        idRipple={id ?? ""}
        className={styles["link"]}
        paddingLess
        isOnLineOnly={isOnLineOnly}
        variant={LinkButton.variant.clear}
        title={title ?? label}
        target={isExternal ? "_blank" : undefined}
        // href to prevent error "no href value"
        href={href}>
        {label}
      </LinkButton>
    );
  });

  const withSearch = isSetRPRAC || isSetCONFLICT || isSetTESTIMONIES;
  const btnSearch = (
    <LinkButton
      paddingLess
      className={classnames(styles["link"], styles["search"])}
      title={"search"}
      href={NEXT_URLS.searchLocal}>
      <IconView icon={EgwWebFont.search} />
    </LinkButton>
  );

  const jsxBtnTheme = landingBookViewConfig?.hasDarkMode && (
    <ButtonTheme classNameButton={classnames(styles["link"], styles["theme-icon"])} />
  );

  let rightSideBtn1;
  if (isSetRPRAC || isSetCONFLICT || isSetTESTIMONIES) {
    rightSideBtn1 = isReaderPage ? (
      jsxBtnTheme
    ) : (
      <Button
        title={t("share")}
        className={classnames(styles["link"], LANDING_SELECTORS.buttonShare.substring(1))}
        onClick={() => {
          dispatch(
            systemActions.actionCopy(window.location.origin, {
              successMessage: t("linkCopied"),
              errorMessage: t("linkNotCopied"),
            }),
          );
        }}>
        <IconView icon={EgwWebFont.share} />
      </Button>
    );
  } else {
    rightSideBtn1 = shareData ? (
      <ButtonShare
        text={shareData.text}
        refCode={shareData.refCode}
        title={shareData.title}
        url={shareData.url}
        className={styles["link"]}
      />
    ) : (
      jsxBtnTheme
    );
  }

  return (
    <header
      className={classnames(styles["root"], {
        [styles["big-landing-header"]]: isBigLanding,
        [styles["asi-header"]]: isSetRPRAC,
      })}>
      <Container className={styles["inner"]}>
        <div className={styles["btn-left"]}>
          <Button
            id={LANDING_SELECTORS.btnOpen.substring(1)}
            className={styles["btn-open"]}
            active={isOpen}
            title={t("menu")}
            onClick={toggle}>
            <IconView icon={EgwWebFont["burger-menu"]} />
          </Button>

          <div id={EL_ID_PORTAL_HEADER_ACTIONS_LEFT} />
        </div>

        {!isBigLanding && (
          <Logotype
            className={styles["header__logo"]}
            onClick={(e) => {
              e.preventDefault();

              router.push(NEXT_URLS.home, undefined, {
                shallow: true,
              });
            }}
            isShowRegistered={false}
            text="Ellen G. White"
          />
        )}

        {withSearch && btnSearch}

        {isBigLanding && <div></div>}

        <div
          className={classnames(styles["navigation"], {
            [styles["open"]]: isOpen,
            [styles["with-btn-install"]]: !isBigLanding && !!btnInstallPWA,
            [styles["with-btn-theme"]]: !isBigLanding && !!rightSideBtn1,
            [styles["with-btn-search"]]: withSearch,
          })}>
          {jsxLinks}
          <LanguageUISelector render="small" className={styles["language-selector"]}/>
          {!isBigLanding && rightSideBtn1}
          {!isBigLanding && btnInstallPWA}
          {withSearch && btnSearch}
        </div>

        <div id={EL_ID_PORTAL_HEADER_ACTIONS} />
      </Container>

      {!isOpen && (
        <PanelHeaderMenu
          isOpen={false}
          isHiddenByCSS={LANDING_SELECTORS.mobileMenuBlock.substring(1)}
          links={linksFinal}
          onClickLink={handleClickItem}
          onClose={toggle}
        />
      )}

      <PanelHeaderMenu
        isOpen={isOpen}
        links={linksFinal}
        onClickLink={handleClickItem}
        onClose={toggle}
      />
    </header>
  );
};

HeaderLanding.propTypes = {
  bookId: PropTypes.string,
};

export default HeaderLanding;
